import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  DollarSign,
  Shield,
  Award,
  FileText,
  Calculator,
  Target,
  Briefcase,
  Download,
  ChevronRight,
  Building,
  Globe
} from 'lucide-react';
import { fetchHealth, fetchMetrics } from '../lib/api';

interface ROIInputs {
  teamSize: number;
  avgSalary: number;
  hoursPerWeek: number;
  currentEfficiency: number;
}

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  badge?: string;
  color: string;
  popular?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    name: "Starter",
    price: "$500",
    description: "Perfect for small teams getting started with AI agents",
    features: [
      "10 concurrent agents",
      "Standard module library",
      "Basic persona templates",
      "Email support",
      "99.5% uptime SLA",
      "Standard integrations"
    ],
    badge: "🟢 Entry Level",
    color: "border-green-600 text-green-400"
  },
  {
    name: "Professional",
    price: "$2,500",
    description: "Advanced capabilities for growing enterprises",
    features: [
      "50 concurrent agents",
      "Full module access (130+ modules)",
      "Custom persona development",
      "Priority support",
      "99.9% uptime SLA",
      "Advanced integrations",
      "Real-time analytics",
      "Tiered access controls"
    ],
    badge: "🔵 Most Popular",
    color: "border-blue-600 text-blue-400",
    popular: true
  },
  {
    name: "Enterprise",
    price: "$10,000+",
    description: "Complete platform with custom integrations",
    features: [
      "Unlimited agents",
      "Custom module development",
      "Dedicated account manager",
      "24/7 premium support",
      "High availability design",
      "SOC 2 readiness",
      "Custom integrations",
      "White-label options",
      "Advanced security features"
    ],
    badge: "🔴 Enterprise",
    color: "border-red-600 text-red-400"
  },
  {
    name: "White-Label License",
    price: "$100,000",
    description: "Complete platform licensing for resellers",
    features: [
      "Full source code access",
      "Unlimited deployment rights",
      "Custom branding",
      "Revenue sharing model",
      "Technical training included",
      "Ongoing updates",
      "Implementation support",
      "Partner certification"
    ],
    badge: "🏷️ Partner",
    color: "border-purple-600 text-purple-400"
  }
];

const complianceCertifications = [
  {
    name: "SOC2 Type 1",
    status: "✅ Implemented",
    description: "Security controls and operational effectiveness",
    timeline: "Completed Q1 2025"
  },
  {
    name: "SOC2 Type 2",
    status: "🔄 In Progress",
    description: "6-month operational effectiveness audit",
    timeline: "Target: Q2 2025"
  },
  {
    name: "GDPR Compliance",
    status: "✅ Ready",
    description: "EU data protection and privacy protocols",
    timeline: "Active since launch"
  },
  {
    name: "ISO 27001",
    status: "📋 Planned",
    description: "Information security management systems",
    timeline: "Target: Q3 2025"
  },
  {
    name: "HIPAA Ready",
    status: "🏥 Available",
    description: "Healthcare data protection capabilities",
    timeline: "Enterprise tier only"
  }
];

export default function SalesCommandBriefing() {
  const [roiInputs, setROIInputs] = useState<ROIInputs>({
    teamSize: 10,
    avgSalary: 120000,
    hoursPerWeek: 20,
    currentEfficiency: 65
  });

  const [calculatedROI, setCalculatedROI] = useState({
    hoursSaved: 0,
    costSavings: 0,
    roi: 0,
    paybackPeriod: 0
  });

  const [platformStats, setPlatformStats] = useState({
    moduleCount: 147,
    agentCount: 35,
    uptime: '99.97%',
    securityStatus: 'Active',
    systemStatus: 'operational',
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load real API data with simulated fallback
  useEffect(() => {
    loadFromAPI();
    pollRef.current = setInterval(loadFromAPI, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    calculateROI();
  }, [roiInputs]);

  const loadFromAPI = async () => {
    const [healthData, metricsData] = await Promise.all([
      fetchHealth(),
      fetchMetrics(),
    ]);

    if (healthData || metricsData) {
      setPlatformStats(prev => {
        const updated = { ...prev };
        if (healthData) {
          updated.systemStatus = healthData.status || 'operational';
          if (healthData.uptime) updated.uptime = typeof healthData.uptime === 'number' ? `${healthData.uptime.toFixed(2)}%` : String(healthData.uptime);
          if (healthData.securityStatus) updated.securityStatus = healthData.securityStatus.status || healthData.securityStatus || 'Active';
        }
        if (metricsData) {
          if (metricsData.capsules?.total != null) updated.moduleCount = metricsData.capsules.total;
          else if (metricsData.totalCapsules != null) updated.moduleCount = metricsData.totalCapsules;
          if (metricsData.blackbox?.total != null) updated.agentCount = metricsData.blackbox.total;
          else if (metricsData.totalBlackbox != null) updated.agentCount = metricsData.totalBlackbox;
          if (metricsData.uptime) updated.uptime = typeof metricsData.uptime === 'number' ? `${metricsData.uptime.toFixed(2)}%` : String(metricsData.uptime);
        }
        return updated;
      });
    }
  };

  const calculateROI = () => {
    const hourlyRate = roiInputs.avgSalary / (52 * 40); // Annual salary to hourly
    const inefficiencyHours = roiInputs.hoursPerWeek * (100 - roiInputs.currentEfficiency) / 100;
    const agentomyEfficiency = 85; // Agentomy achieves 85% efficiency
    const hoursSavedPerPerson = inefficiencyHours * (agentomyEfficiency - roiInputs.currentEfficiency) / 100;
    const totalHoursSaved = hoursSavedPerPerson * roiInputs.teamSize * 4.33; // Monthly
    const monthlySavings = totalHoursSaved * hourlyRate;
    const agentomyCost = roiInputs.teamSize <= 10 ? 500 : roiInputs.teamSize <= 50 ? 2500 : 10000;
    const netROI = ((monthlySavings - agentomyCost) / agentomyCost) * 100;
    const paybackMonths = agentomyCost / (monthlySavings - agentomyCost);

    setCalculatedROI({
      hoursSaved: Math.round(totalHoursSaved),
      costSavings: Math.round(monthlySavings),
      roi: Math.round(netROI),
      paybackPeriod: Math.max(0.1, paybackMonths)
    });
  };

  const exportBriefingDeck = () => {
    const briefingData = {
      timestamp: new Date().toISOString(),
      platformStats,
      pricing: pricingTiers,
      roi: calculatedROI,
      compliance: complianceCertifications,
      customInputs: roiInputs
    };
    
    const dataStr = JSON.stringify(briefingData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `agentomy-briefing-${new Date().toISOString().split('T')[0]}.json`);
    linkElement.click();
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            📊 Agentomy Sales Command Briefing
          </h2>
          <p className="text-[#A8A29E]">Enterprise-ready AI agent orchestration platform -- {platformStats.moduleCount} modules | {platformStats.agentCount} core agents | Uptime {platformStats.uptime} | Security {platformStats.securityStatus}</p>
        </div>
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            onClick={exportBriefingDeck}
            className="border-cyan-600 text-cyan-400 hover:bg-cyan-900"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Deck
          </Button>
          <Badge variant="outline" className="text-green-400 border-green-400">
            VC Ready
          </Badge>
        </div>
      </div>

      {/* Market Positioning */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="w-5 h-5 mr-2 text-cyan-400" />
            Market Position & Value Proposition
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-[#292524] rounded-lg">
              <Building className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <h4 className="font-semibold text-white">Enterprise Focus</h4>
              <p className="text-[#A8A29E] text-sm">Purpose-built for enterprise security and compliance requirements</p>
            </div>
            <div className="text-center p-4 bg-[#292524] rounded-lg">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <h4 className="font-semibold text-white">Security First</h4>
              <p className="text-[#A8A29E] text-sm">Military-grade security -- Security Layer {platformStats.securityStatus}, {platformStats.uptime} uptime, TrustChain verified</p>
            </div>
            <div className="text-center p-4 bg-[#292524] rounded-lg">
              <Globe className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <h4 className="font-semibold text-white">Scalable Platform</h4>
              <p className="text-[#A8A29E] text-sm">From 10 agents to unlimited -- currently {platformStats.moduleCount} modules, {platformStats.agentCount} core agents</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Tiers */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-green-400" />
            Pricing Tiers & Revenue Model
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {pricingTiers.map((tier) => (
              <div 
                key={tier.name}
                className={`relative p-6 rounded-lg border-2 ${tier.color} bg-[#292524] ${
                  tier.popular ? 'ring-2 ring-blue-500' : ''
                } transition-all hover:scale-105`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <div className="text-xs font-medium text-[#A8A29E] mb-1">{tier.badge}</div>
                  <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                  <div className="text-3xl font-bold mt-2">
                    <span className="text-cyan-400">{tier.price}</span>
                    <span className="text-sm text-[#A8A29E]">/month</span>
                  </div>
                </div>
                
                <p className="text-[#A8A29E] text-sm mb-4 text-center">{tier.description}</p>
                
                <ul className="space-y-2">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-sm">
                      <ChevronRight className="w-4 h-4 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-[#A8A29E]">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className="w-full mt-6" 
                  variant={tier.popular ? "default" : "outline"}
                >
                  {tier.name === "White-Label License" ? "Contact Sales" : "Get Started"}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ROI Calculator */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calculator className="w-5 h-5 mr-2 text-yellow-400" />
            ROI Calculator & Business Impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Your Organization</h4>
              
              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">Team Size</label>
                <Input
                  type="number"
                  value={roiInputs.teamSize}
                  onChange={(e) => setROIInputs({...roiInputs, teamSize: parseInt(e.target.value) || 0})}
                  className="bg-[#292524] border-[#4A443D] text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">Average Salary ($)</label>
                <Input
                  type="number"
                  value={roiInputs.avgSalary}
                  onChange={(e) => setROIInputs({...roiInputs, avgSalary: parseInt(e.target.value) || 0})}
                  className="bg-[#292524] border-[#4A443D] text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">Hours/Week on Repetitive Tasks</label>
                <Input
                  type="number"
                  value={roiInputs.hoursPerWeek}
                  onChange={(e) => setROIInputs({...roiInputs, hoursPerWeek: parseInt(e.target.value) || 0})}
                  className="bg-[#292524] border-[#4A443D] text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">Current Efficiency (%)</label>
                <Input
                  type="number"
                  max="100"
                  value={roiInputs.currentEfficiency}
                  onChange={(e) => setROIInputs({...roiInputs, currentEfficiency: parseInt(e.target.value) || 0})}
                  className="bg-[#292524] border-[#4A443D] text-white"
                />
              </div>
            </div>
            
            {/* Results Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Projected Impact</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#292524] p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-400">{calculatedROI.hoursSaved}</div>
                  <div className="text-sm text-[#A8A29E]">Hours Saved/Month</div>
                </div>
                
                <div className="bg-[#292524] p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-cyan-400">${calculatedROI.costSavings.toLocaleString()}</div>
                  <div className="text-sm text-[#A8A29E]">Monthly Savings</div>
                </div>
                
                <div className="bg-[#292524] p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-400">{calculatedROI.roi}%</div>
                  <div className="text-sm text-[#A8A29E]">Monthly ROI</div>
                </div>
                
                <div className="bg-[#292524] p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-400">{calculatedROI.paybackPeriod.toFixed(1)}</div>
                  <div className="text-sm text-[#A8A29E]">Payback (Months)</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-900 to-blue-900 p-4 rounded-lg mt-6">
                <h5 className="font-semibold text-white mb-2">💰 Annual Impact</h5>
                <ul className="text-sm text-gray-200 space-y-1">
                  <li>• Total Annual Savings: <strong>${(calculatedROI.costSavings * 12).toLocaleString()}</strong></li>
                  <li>• Productivity Increase: <strong>20-35%</strong></li>
                  <li>• Time to Value: <strong>2-4 weeks</strong></li>
                  <li>• Employee Satisfaction: <strong>+40%</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance & Certifications */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2 text-green-400" />
            Compliance & Security Certifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceCertifications.map((cert) => (
              <div key={cert.name} className="bg-[#292524] p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-white">{cert.name}</h4>
                  <span className="text-sm">{cert.status}</span>
                </div>
                <p className="text-[#A8A29E] text-sm mb-2">{cert.description}</p>
                <div className="text-xs text-gray-500">{cert.timeline}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-blue-900 rounded-lg">
            <h4 className="font-semibold text-white mb-2">🏆 Enterprise Security Features</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-200">
              <div>• Tiered Access Controls</div>
              <div>• TrustChain Audit Logs</div>
              <div>• End-to-End Encryption</div>
              <div>• Role-Based Permissions</div>
              <div>• Container Isolation</div>
              <div>• Network Segmentation</div>
              <div>• Regular Penetration Testing</div>
              <div>• 24/7 Security Monitoring</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Competitive Advantage */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Award className="w-5 h-5 mr-2 text-yellow-400" />
            Competitive Advantage & Differentiators
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-white mb-4">🎯 Key Differentiators</h4>
              <ul className="space-y-3 text-[#A8A29E]">
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Module Architecture:</strong> Modular, swappable agent capabilities</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Security Layer:</strong> Military-grade access control system</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>TrustChain Auditing:</strong> Complete operational transparency</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Persona System:</strong> Tiered behavioral control framework</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">📈 Market Opportunity</h4>
              <div className="space-y-3">
                <div className="bg-[#292524] p-3 rounded">
                  <div className="text-lg font-bold text-green-400">$150B</div>
                  <div className="text-sm text-[#A8A29E]">AI Automation Market by 2025</div>
                </div>
                <div className="bg-[#292524] p-3 rounded">
                  <div className="text-lg font-bold text-cyan-400">85%</div>
                  <div className="text-sm text-[#A8A29E]">Enterprise AI Adoption Rate</div>
                </div>
                <div className="bg-[#292524] p-3 rounded">
                  <div className="text-lg font-bold text-yellow-400">3.2x</div>
                  <div className="text-sm text-[#A8A29E]">Average ROI on AI Investment</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="bg-gradient-to-r from-cyan-900 to-blue-900 border-cyan-500">
        <CardContent className="p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Operations?</h3>
          <p className="text-gray-200 mb-6">Join leading enterprises already using Agentomy to automate their most critical workflows</p>
          <div className="flex justify-center space-x-4">
            <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700">
              <Briefcase className="w-4 h-4 mr-2" />
              Schedule Demo
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Download Whitepaper
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}